import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { PlatformHealth } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import StatusChip from "../../StatusChip.tsx";
import { StatusLabel } from "../../StatusIcon.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import { importStatus } from "./importStats.ts";

/** Each CRM connection's newest sync and the news feeds that keep failing. */
export function ConnectionsPanel({ health }: { health: PlatformHealth }) {
  const { crm_connections: crm, news_feeds: feeds } = health;
  if (crm.length === 0 && feeds.worst.length === 0) return null;
  return (
    <Panel title="Connections" subtitle="CRM portals and failing news feeds" disablePadding>
      <ScrollTable>
        <TableBody>
          {crm.map((c) => {
            const status = importStatus(c.status);
            return (
              <TableRow key={c.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {c.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {c.provider === "hubspot" ? "HubSpot" : c.provider}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={c.last_error ?? ""} disableHoverListener={!c.last_error}>
                    <Box component="span">
                      <StatusLabel kind={status.kind} label={status.label} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <RelativeTime value={c.last_success_at} />
                </TableCell>
              </TableRow>
            );
          })}
          {feeds.worst.map((f) => (
            <TableRow key={f.name}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {f.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  News feed
                </Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={f.last_error ?? ""} disableHoverListener={!f.last_error}>
                  <Box component="span">
                    <StatusLabel
                      kind="error"
                      label={`${formatNumber(f.consecutive_failures)} failures in a row`}
                    />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <RelativeTime value={f.last_success_at} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}

/** The newest file uploads (company CSVs, contacts, ownership, ATS feeds). */
export function RecentUploadsPanel({ imports }: { imports: PlatformHealth["csv_imports"] }) {
  if (imports.length === 0) return null;
  return (
    <Panel title="Recent uploads" disablePadding>
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
        {imports.map((i) => (
          <Stack key={i.id} direction="row" sx={{ alignItems: "center", gap: 1.5, px: 2, py: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {i.file_name}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {i.imported_count != null ? `${formatNumber(i.imported_count)} imported · ` : ""}
                <RelativeTime value={i.created_at} />
              </Typography>
            </Box>
            <StatusChip kind="job" value={i.status} />
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}
